import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { filter, Subscription } from "rxjs";
import { User } from "../../../models/user.interface";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { SUBSCRIPTION_TYPES, USER_TYPES } from "../../constants";
import { AuthService } from "../../../services/auth.service";

@Component({
  selector: "app-sidebar",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
  animations: [
    trigger("menuToggle", [
      state(
        "expanded",
        style({
          width: "250px",
        })
      ),
      state(
        "collapsed",
        style({
          width: "50px",
        })
      ),
      state(
        "collapsedResponsive",
        style({
          width: "0px",
          padding: 0,
          opacity: 0,
          visibility: "hidden",
        })
      ),
      state(
        "expandedResponsive",
        style({
          width: "100vw",
          height: "100vh",
          margin: 0,
          borderRadius: 0,
        })
      ),
      transition("expanded <=> collapsedResponsive", [
        animate("200ms ease-in-out"),
      ]),
      transition("expanded <=> collapsed", [animate("200ms ease-in-out")]),
      transition("expandedResponsive <=> collapsedResponsive", [
        animate("200ms ease-in-out"),
      ]),
    ]),
  ],
})
export class SidebarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() user!: User;
  @Input() isCollapsed = false;
  @Input() isResponsiveMenu = false;
  @Output() toggleMenuEvent = new EventEmitter<void>();
  @Output() logoutEvent = new EventEmitter<void>();
  @Output() navigationEvent = new EventEmitter<{
    route: string;
    option: any;
    subOption?: any;
  }>();

  activeOption: any = null;
  activeSubOption: any = null;
  showProfileSubmenu = false; // Variable para controlar la visibilidad del submenú de perfil
  private routerSubscription: Subscription | undefined;
  
  // Cache para evitar cálculos repetidos
  private _currentWeek: number | null = null;
  private _permissionCache: Map<number, boolean> = new Map();
  private _routeDisabledCache: Map<string, boolean> = new Map();

  constructor(private router: Router, private authService: AuthService) {
    const authUser = this.authService.getUser();
    if (authUser) {
      if (authUser.type.id == USER_TYPES.Administrator.id) {
        this.menuOptions = this.menuOptionsBase.filter((x) =>
          this.optionsAdmin.includes(x.id)
        );
      }
      if (authUser.type.id == USER_TYPES.Coach.id) {
        this.menuOptions = this.menuOptionsBase.filter((x) =>
          this.optionsCoach.includes(x.id)
        );
      }
      if (authUser.type.id == 4) { // Mentor
        this.menuOptions = this.menuOptionsBase.filter((x) =>
          this.optionsCoach.includes(x.id) // Usar las mismas opciones que Coach
        );
      }
      if (authUser.type.id == USER_TYPES.Expert.id) {
        this.menuOptions = this.menuOptionsBase
          .filter((x) => this.optionsExpert.includes(x.id))
          .map((option) => {
            const clonedOption = {
              ...option,
              subOptions: option.subOptions
                ? option.subOptions.map((subOption: any) => ({ ...subOption }))
                : undefined,
            };

            if (clonedOption.id === "dashboard") {
              clonedOption.route = "dashboard-expert";
            }

            return clonedOption;
          });
      }
      if (authUser.type.id == USER_TYPES.Customer.id) {
        if (
          authUser.type.suscriptionType?.id ==
          SUBSCRIPTION_TYPES.Freemium.id.toString()
        ) {
          this.menuOptions = this.menuOptionsBase.filter((x) =>
            this.optionsCustomerFreemium.includes(x.id)
          );
        }
        if (
          authUser.type.suscriptionType?.id ==
          SUBSCRIPTION_TYPES.Professional.id.toString()
        ) {
          this.menuOptions = this.menuOptionsBase.filter((x) =>
            this.optionsCustomerProfessional.includes(x.id)
          );
        }
        if (
          authUser.type.suscriptionType?.id ==
          SUBSCRIPTION_TYPES.Executive.id.toString()
        ) {
          this.menuOptions = this.menuOptionsBase.filter((x) =>
            this.optionsCustomerExecutive.includes(x.id)
          );
        }
      }
      
      // Aplicar filtro adicional basado en isVisible
      this.menuOptions = this.menuOptions.filter((option) => 
        this.isMenuOptionVisible(option)
      );
    }
  }

  toggleMenu() {
    this.toggleMenuEvent.emit();
  }

  // Método para mostrar/ocultar el submenú del perfil
  toggleProfileSubmenu(event: Event) {
    event.stopPropagation();
    this.showProfileSubmenu = !this.showProfileSubmenu;

    // Cerrar el submenú cuando se hace clic fuera de él
    if (this.showProfileSubmenu) {
      setTimeout(() => {
        document.addEventListener("click", this.closeProfileSubmenu);
      }, 0);
    }
  }

  closeProfileSubmenu = () => {
    this.showProfileSubmenu = false;
    document.removeEventListener("click", this.closeProfileSubmenu);
  };

  ngOnDestroy() {
    document.removeEventListener("click", this.closeProfileSubmenu);
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    // Limpiar caché
    this.clearCache();
  }

  // Función para limpiar el caché
  private clearCache(): void {
    this._currentWeek = null;
    this._permissionCache.clear();
    this._routeDisabledCache.clear();
  }

  // Función para limpiar caché cuando cambie el usuario
  private clearCacheOnUserChange(): void {
    this.clearCache();
  }

  // Función pública para forzar la limpieza del caché (útil para debugging)
  public forceRefreshCache(): void {
    console.log('🔄 Forcing cache refresh...');
    this.clearCache();
  }

  // Método para navegar a las diferentes secciones del perfil
  navigateToProfileSection(section: string, event: Event) {
    event.stopPropagation();
    this.showProfileSubmenu = false;

    switch (section) {
      case "personalData":
        this.router.navigate(["/dashboard/edit-profile"]);
        break;
      case "professionalData":
        this.router.navigate(["/dashboard/edit-profile"], {
          fragment: "professional",
        });
        break;
      case "mentorProfile":
        this.router.navigate(["/dashboard/profile-mentor"]);
        break;
      case "plan":
        this.router.navigate(["/dashboard/plans"]);
        break;
      case "notifications":
        this.router.navigate(["/dashboard/notifications"]);
        break;
      case "accountManagement":
        this.router.navigate(["/dashboard/account-settings"]);
        break;
      case "switchAccount":
        // Aquí podría ir lógica para cambiar de cuenta
        console.log("Cambiar de cuenta");
        break;
      default:
        this.router.navigate(["/dashboard/edit-profile"]);
    }
  }

  redirectProfile() {
    this.router.navigate(["/dashboard/edit-profile"]);
  }

  toNavigate(route: string, option: any, subOption?: any) {
    // Ocultar submenú de perfil si está visible
    this.showProfileSubmenu = false;

    // No navegar si la subopción es un encabezado
    if (subOption && subOption.isHeader) {
      return;
    }

    // Verificar si la ruta está deshabilitada pero permitir la navegación con mensaje informativo
    if (this.isRouteDisabled(route)) {
      // Si es usuario Freemium y la ruta está bloqueada, mostrar un mensaje o redirigir a planes
      if (this.isFreemiumUser() && route !== "plans" && route !== "ayuda") {
        // Opcionalmente, podríamos redirigir al usuario a la página de planes aquí
        // this.router.navigate(['/dashboard/plans']);
        console.log(
          "Ruta bloqueada para usuarios Freemium. Actualice su plan para acceder."
        );
        return; // No continuar con la navegación
      }

      // Si es usuario Executive y la ruta está bloqueada por semana o permisos
      if (this.isExecutiveUser()) {
        const currentWeek = this.getCurrentWeek();
        console.log(
          `Ruta deshabilitada para usuario Executive. Semana actual: ${currentWeek}. Esta opción estará disponible cuando corresponda según su plan.`
        );
        return; // No continuar con la navegación
      }

      // Para otros usuarios, si la ruta no está permitida, simplemente no navegar
      console.log("Ruta no permitida para este tipo de usuario.");
      return;
    }

    // Permitir navegación a todos los menús permitidos
    if (!(this.activeOption === option)) {
      this.menuOptions.forEach((opt) => (opt.expanded = false));
    }

    // Si es un coach o mentor y está navegando al dashboard, redirigir al dashboard de coach
    if (route === "dashboard-menu" && (this.isCoach() || this.isMentor())) {
      route = "dashboard-coach";
    }

    // Si es un experto y está navegando al dashboard, redirigir al dashboard-expert
    if (route === "dashboard-menu" && this.isExpert()) {
      route = "dashboard-expert";
    }

    // Si es un usuario Executive y está navegando al dashboard, redirigir al dashboard-executive
    if (route === "dashboard-menu" && this.isExecutiveUser()) {
      route = "dashboard-executive";
    }

    // Si es un mentor/coach y está navegando a "Mi perfil", redirigir a profile-mentor
    if (route === "edit-profile" && (this.isCoach() || this.isMentor())) {
      route = "profile-mentor";
    }

    this.navigationEvent.emit({ route, option, subOption });
    this.activeOption = option;
    this.activeSubOption = subOption || null;
  }

  logOut() {
    this.logoutEvent.emit();
  }

  toggleSubOptions(option: any) {
    option.expanded = !option.expanded;
    this.activeOption = option;
    this.activeSubOption = null;
  }

  isAllowedRoute(route: string): boolean {
    if(!this.user || !this.user.type) {
      return false; // No permitir rutas si el usuario o su tipo no están definidos
    }
    if(!route || route === "") {
      return false; // No permitir rutas vacías o nulas
    }

    // Para usuarios Freemium, solo permitir rutas específicas
    if (this.isFreemiumUser()) {
      const allowedRoutes = ["dashboard-menu", "edit-profile", "results", "objectives", "academy","mentors","plans","mi-mentor","prd","life-circle","life-map","visionboard","priorizacion","revision","ikigai"];
      return allowedRoutes.includes(route);
    }

    // Para usuarios Executive, aplicar filtros de semana y permisos
    if (this.isExecutiveUser()) {
      return this.isExecutiveRouteAllowed(route);
    }

    // Para otros tipos de usuarios, permitir todas las rutas (se filtran por menuOptions)
    return true;
  }

  // Función para verificar si una ruta debe estar deshabilitada (pero visible)
  isRouteDisabled(route: string): boolean {
    // Verificar caché primero
    if (this._routeDisabledCache.has(route)) {
      return this._routeDisabledCache.get(route)!;
    }

    console.log(`🚫 Checking if route ${route} should be disabled`);

    if(!this.user || !this.user.type) {
      console.log(`❌ No user or user type found`);
      this._routeDisabledCache.set(route, true);
      return true; // Deshabilitar si el usuario o su tipo no están definidos
    }
    if(!route || route === "") {
      console.log(`❌ Empty route`);
      this._routeDisabledCache.set(route, true);
      return true; // Deshabilitar rutas vacías o nulas
    }

    let isDisabled = false;

    // Para usuarios Executive, verificar si debe estar deshabilitada
    if (this.isExecutiveUser()) {
      console.log(`👑 User is Executive, checking route allowance`);
      const isAllowed = this.isExecutiveRouteAllowed(route);
      isDisabled = !isAllowed;
      console.log(`👑 Route ${route} allowed: ${isAllowed}, disabled: ${isDisabled}`);
    }
    // Para usuarios Freemium, verificar si la ruta está en la lista permitida
    else if (this.isFreemiumUser()) {
      console.log(`🆓 User is Freemium`);
      const allowedRoutes = ["dashboard-menu", "edit-profile", "results", "objectives", "academy","mentors","plans","mi-mentor","prd","life-circle","life-map","visionboard","priorizacion","revision","ikigai"];
      isDisabled = !allowedRoutes.includes(route);
      console.log(`🆓 Route ${route} in allowed list: ${allowedRoutes.includes(route)}, disabled: ${isDisabled}`);
    }
    // Para otros tipos de usuarios, todas las rutas están habilitadas
    else {
      console.log(`👤 User is other type, route enabled`);
      isDisabled = false;
    }

    console.log(`🚫 Final result for route ${route}: disabled = ${isDisabled}`);

    // Guardar en caché
    this._routeDisabledCache.set(route, isDisabled);
    return isDisabled;
  }

  // Función para verificar si una ruta está permitida para usuarios Executive
  isExecutiveRouteAllowed(route: string): boolean {
    const currentWeek = this.getCurrentWeek();
    console.log(`🔍 isExecutiveRouteAllowed - Route: ${route}, Current Week: ${currentWeek}`);

    switch (route) {
      case 'life-circle':
      case 'prd':
        // Step 1: Análisis profundo - Sem 1 y 2, permission 0
        // Permitir si la semana actual es >= 1 (siempre disponible después de la semana 1)
        const step1Allowed = currentWeek >= 0 && this.isPermissionActive(0);
        console.log(`📊 Step 1 (${route}): Week ${currentWeek} (>=0), Permission 0: ${this.isPermissionActive(0)}, Allowed: ${step1Allowed}`);
        return step1Allowed;
      
      case 'identity-map':
        // Step 2: Mapa de identidad - Sem 3, permission 1
        // Permitir si la semana actual es >= 3 (disponible desde la semana 3 en adelante)
        const step2Allowed = currentWeek >= 3 && this.isPermissionActive(1);
        console.log(`📊 Step 2 (${route}): Week ${currentWeek} (>=3), Permission 1: ${this.isPermissionActive(1)}, Allowed: ${step2Allowed}`);
        return step2Allowed;
      
      case 'visionboard':
        // Step 5: Visionboard - Sem 5, permission 3
        // Permitir si la semana actual es >= 5 (disponible desde la semana 5 en adelante)
        const step5Allowed = currentWeek >= 5 && this.isPermissionActive(3);
        console.log(`📊 Step 5 (${route}): Week ${currentWeek} (>=5), Permission 3: ${this.isPermissionActive(3)}, Allowed: ${step5Allowed}`);
        return step5Allowed;
      
      case 'priorizacion':
      case 'revision':
        // Step 6: PRD - Sem 7-9, permission 5
        // Permitir si la semana actual es >= 7 (disponible desde la semana 7 en adelante)
        const step6Allowed = currentWeek >= 7 && this.isPermissionActive(5);
        console.log(`📊 Step 6 (${route}): Week ${currentWeek} (>=7), Permission 5: ${this.isPermissionActive(5)}, Allowed: ${step6Allowed}`);
        return step6Allowed;
      
      default:
        // Permitir otras rutas que no están en los filtros específicos
        console.log(`✅ Default route (${route}): Allowed`);
        return true;
    }
  }

  // Función para verificar si una subopción debe estar deshabilitada para usuarios Executive
  isExecutiveSubOptionDisabled(subOption: any): boolean {
    if (!this.isExecutiveUser()) {
      return false; // Si no es Executive, usar la lógica normal
    }

    const currentWeek = this.getCurrentWeek();

    switch (subOption.route) {
      case 'life-circle':
      case 'prd':
        // Step 1: Análisis profundo - Sem 1 y 2, permission 0
        return !((currentWeek >= 1 && currentWeek <= 2) && this.isPermissionActive(0));
      
      case 'identity-map':
        // Step 2: Mapa de identidad - Sem 3, permission 1
        return !((currentWeek >= 3 && currentWeek <= 3) && this.isPermissionActive(1));
      
      case 'visionboard':
        // Step 5: Visionboard - Sem 5, permission 3
        return !((currentWeek >= 5 && currentWeek <= 5) && this.isPermissionActive(3));
      
      case 'priorizacion':
      case 'revision':
        // Step 6: PRD - Sem 7-9, permission 5
        return !((currentWeek >= 7 && currentWeek <= 9) && this.isPermissionActive(5));
      
      default:
        return false; // No deshabilitar otras opciones
    }
  }

  isMenuOptionVisible(option: any): boolean {
    // Aplicar filtros específicos para usuarios Executive
    if (this.isExecutiveUser()) {
      return this.isExecutiveMenuOptionVisible(option);
    }

    // Lógica normal para otros tipos de usuario
    if (option.isVisible) {
      return option.isVisible();
    }
    return true;
  }

  // Método para determinar si un menú debe tener fondo rojo
  shouldHaveRedButton(option: any): boolean {
    // Si es el menú "Descúbrete" y tiene un sub-option activo
    if (option.id === "descubrete" && this.activeOption === option && this.activeSubOption) {
      return true;
    }
    return false;
  }

  // Método para verificar si el usuario es administrativo (type.id 3)
  isAdminUserType3(): boolean {
    return !!(this.user && this.user.type && this.user.type.id === 3);
  }

  isCustomer(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === USER_TYPES.Customer.id
    );
  }

  // Método para verificar si el usuario tiene plan Freemium (cualquier tipo)
  isFreemiumUser(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.suscriptionType &&
      this.user.type.suscriptionType.id === "1"
    );
  }

  // Nueva función para Cliente Freemium
  isClienteFreemium(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 1 &&
      this.user.type.suscriptionType &&
      this.user.type.suscriptionType.id === "1"
    );
  }

  // Nueva función para Cliente Premium
  isClientePremium(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 1 &&
      this.user.type.suscriptionType &&
      (this.user.type.suscriptionType.id === "2" ||
        this.user.type.suscriptionType.id === "3")
    );
  }

  getProfilePhotoUrl(): string {
    if (this.user?.personalSection?.photoBase64) {
      return this.user.personalSection.photoBase64;
    }
    return this.user?.photo || "assets/img/user-photo-default.png";
  }

  onPhotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = "assets/img/user-photo-default.png";
  }

  isCoach(): boolean {
    return !!(this.user && this.user.type && this.user.type.id === 2);
  }

  isMentor(): boolean {
    return !!(this.user && this.user.type && this.user.type.id === 4);
  }

  isExpert(): boolean {
    return !!(this.user && this.user.type && this.user.type.id === USER_TYPES.Expert.id);
  }

  isExecutiveUser(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 1 && // Customer
      this.user.type.suscriptionType &&
      this.user.type.suscriptionType.id === SUBSCRIPTION_TYPES.Executive.id.toString()
    );
  }

  // Función para obtener la semana actual del usuario Executive
  getCurrentWeek(): number {
    // Si ya se calculó, devolver el valor en caché
    if (this._currentWeek !== null) {
      return this._currentWeek;
    }

    console.log(`📅 Calculating current week for user:`, this.user);
    console.log(`📅 User createdAt:`, this.user?.createdAt);

    if (!this.user?.createdAt) {
      console.log(`📅 No createdAt found, defaulting to week 1`);
      this._currentWeek = 1;
      return this._currentWeek;
    }

    const createdAt = new Date(this.user.createdAt);
    const now = new Date();
    
    console.log(`📅 Created at date:`, createdAt);
    console.log(`📅 Current date:`, now);
    
    // Normalizar ambas fechas al inicio del día en zona local
    const startOfCreatedAt = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calcular la diferencia en milisegundos
    const diffInMs = Math.max(0, startOfToday.getTime() - startOfCreatedAt.getTime());
    
    // Convertir a días naturales transcurridos
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    console.log(`📅 Difference in days:`, diffInDays);
    
    // Calcular la semana (redondeando hacia arriba)
    const week = Math.max(1, Math.floor(diffInDays / 7) + 1);
    
    console.log(`📅 Calculated week:`, week);
    
    // Guardar en caché
    this._currentWeek = week;
    return this._currentWeek;
  }

  // Función para verificar si un permiso está activo según el índice
  isPermissionActive(permissionIndex: number): boolean {
    // Verificar caché primero
    if (this._permissionCache.has(permissionIndex)) {
      return this._permissionCache.get(permissionIndex)!;
    }

    console.log(`🔐 Checking permission ${permissionIndex} for user:`, this.user?.permissions);
    
    if (!this.user?.permissions || !Array.isArray(this.user.permissions)) {
      console.log(`❌ No permissions array found for user`);
      this._permissionCache.set(permissionIndex, false);
      return false;
    }
    
    const permission = this.user.permissions[permissionIndex];
    const isActive = permission && permission.active === true;
    
    console.log(`🔐 Permission ${permissionIndex}:`, permission, `Active: ${isActive}`);
    
    // Guardar en caché
    this._permissionCache.set(permissionIndex, isActive);
    return isActive;
  }

  // Función para verificar si una opción del menú debe estar visible para usuarios Executive
  isExecutiveMenuOptionVisible(option: any): boolean {
    if (!this.isExecutiveUser()) {
      return true; // Si no es Executive, usar la lógica normal
    }

    // Para usuarios Executive, siempre mostrar las opciones principales del menú
    // Los filtros se aplicarán a nivel de subopciones en el template
    return true;
  }

  // Función para verificar si una subopción debe estar visible para usuarios Executive
  isExecutiveSubOptionVisible(subOption: any): boolean {
    if (!this.isExecutiveUser()) {
      return true; // Si no es Executive, usar la lógica normal
    }

    const currentWeek = this.getCurrentWeek();

    switch (subOption.route) {
      case 'life-circle':
      case 'prd':
        // Step 1: Análisis profundo - Sem 1 y 2, permission 0
        return (currentWeek >= 1 && currentWeek <= 2) && this.isPermissionActive(0);
      
      case 'identity-map':
        // Step 2: Mapa de identidad - Sem 3, permission 1
        return (currentWeek >= 3 && currentWeek <= 3) && this.isPermissionActive(1);
      
      case 'visionboard':
        // Step 5: Visionboard - Sem 5, permission 3
        return (currentWeek >= 5 && currentWeek <= 5) && this.isPermissionActive(3);
      
      case 'priorizacion':
      case 'revision':
        // Step 6: PRD - Sem 7-9, permission 5
        return (currentWeek >= 7 && currentWeek <= 9) && this.isPermissionActive(5);
      
      default:
        return true; // Mantener otras opciones
    }
  }

  menuOptions: any[] = [];
  menuOptionsBase: any[] = [
    {
      id: "dashboard",
      title: "Inicio",
      route: "dashboard-menu",
      expanded: false,
      icon: "fa-th-large",
    },
    {
      id: "dashboard-mentor",
      title: "Dashboard Mentor",
      route: "dashboard-coach",
      expanded: false,
      icon: "fa-chalkboard-teacher",
    },
    {
      id: "users-assigned",
      title: "Usuarios asignados",
      route: "users-assigned",
      expanded: false,
      icon: "fa-user-plus",
    },
    {
      id: "onboarding",
      title: "Mi perfil",
      route: "edit-profile",
      icon: "fa-user",
      expanded: false,
    },
    {
      id: "results",
      title: "Rueda de la vida",
      route: "results",
      icon: "fa-compass",
      expanded: false,
    },
    {
      id: "mi-mentor",
      title: "Mi Mentor",
      route: "mi-mentor",
      icon: "fa-user-tie",
      expanded: false,
      isVisible: () => {
        // Solo visible para usuarios Executive que tengan un coach asignado
        const authUser = this.authService.getUser();
        return authUser && 
               authUser.type && 
               authUser.type.id === 1 && // Customer
               authUser.type.suscriptionType && 
               authUser.type.suscriptionType.id === "3" && // Executive
               authUser.coachAssigned && 
               authUser.coachAssigned.id;
      }
    },
    {
      id: "mentores-expertos",
      title: "Expertos",
      route: "mentors",
      icon: "fa-comments",
      expanded: false,
    },
    {
      id: "planificacion",
      title: "Objetivos",
      route: "objectives",
      icon: "fa-folder-minus",
      expanded: false,
    },
    {
      id: "academy",
      title: "Academy",
      route: "academy",
      icon: "fa-graduation-cap",
      expanded: false,
    },
    {
      id: "academy-vista",
      title: "Academy Vista",
      route: "academy-vista",
      icon: "fa-book-open",
      expanded: false,
    },
    {
      id: "programas",
      title: "Suscripción",
      route: "plans",
      icon: "fa-credit-card",
      expanded: false,
    },
      {
        id: "descubrete",
        title: "Descúbrete",
        route: "descubrete",
        icon: "fa-brain",
        subOptions: [
          //{ title: "Análisis profundo", route: "prd" },
          { title: "Análisis profundo", route: "life-circle" },
          { title: "Guía de reflexión personal", route: "prd" },
          { title: "Mapa de identidad", route: "identity-map" },
          { title: "Vision Board", route: "visionboard" },
        ],
        expanded: false,
      },
      {
        id: "plan-vida",
        title: "Plan de vida",
        route: "plan-vida",
        icon: "fa-road",
        subOptions: [
          { title: "Priorización", route: "priorizacion" },
          { title: "Revisión", route: "revision" },
          // { title: "Ikigai", route: "ikigai" },
        ],
        expanded: false,
      },
      
    {
      id: "acompañamiento",
      title: "Acompañamiento",
      route: "",
      icon: "fa-list-check",
      expanded: false,
    },
    {
      id: "desarrollo",
      title: "Desarrollo",
      route: "desarrollo",
      icon: "fa-list-check",
      expanded: false,
      subOptions: [
        { title: "Ikigai", route: "ikigai", icon: "fa-compass" },
        { title: "Financiera", route: "" },
      ],
    },
        {
      id: "herramientas",
      title: "Herramientas",
      route: "herramientas",
      icon: "fa-tools",
      expanded: false,
    },
    {
      id: "agenda",
      title: "Agenda",
      route: "agenda",
      icon: "fa-calendar-alt",
      expanded: false,
    },
    {
      id: "ganancias",
      title: "Ganancias",
      route: "ganancias",
      icon: "fa-chart-line",
      expanded: false,
    },
    {
      id: "configuracion",
      title: "Configuración",
      route: "config",
      icon: "fa-gear",
      expanded: false,
    },
    {
      id: "admin-customers",
      title: "Administración de Usuarios",
      route: "admin/users",
      icon: "fa-gear",
    },
    {
      id: "admin-coaches",
      title: "Administración de Mentores",
      route: "admin/mentors",
      icon: "fa-gear",
    },
    {
      id: "admin-experts",
      title: "Administración de Expertos",
      route: "admin/experts",
      icon: "fa-gear",
    },
    {
      id: "admin-accounts",
      title: "Administración de Cuentas",
      route: "admin/accounts",
      icon: "fa-gear",
    },
    {
      id: "admin-roles",
      title: "Administración de Roles",
      route: "admin/roles",
      icon: "fa-gear",
    },
    {
      id: "admin-videos-app",
      title: "Administración de Videos App",
      route: "admin/videos-config",
      icon: "fa-gear",
    },
    {
      id: "admin-videos-academy",
      title: "Administración de Academy",
      route: "admin/admin-videos",
      icon: "fa-gear",
    },

    {
      id: "reportes",
      title: "Reportes",
      route: "reportes",
      icon: "fa-chart-line",
      expanded: false,
    },

    // {
    //   title: "Ayuda",
    //   route: "ayuda",
    //   icon: "fa-question",
    //   expanded: false,
    //   // Siempre visible para todos
    // },
  ];

  optionsCustomerFreemium = [
    "dashboard", 
    "onboarding", 
    "results", 
    "mentores-expertos", 
    "planificacion", 
    "academy", 
    "programas", 
    "descubrete", 
    "prd", 
    "life-circle", 
    "life-map", 
    "identity-map",
    "visionboard",
    "plan-vida",
    "priorizacion",
    "revision",
    // "ikigai",
    // "configuracion"
  ];
  optionsCustomerProfessional = [
    "dashboard",
    "onboarding",
    "results",
    "mentores-expertos",
    "planificacion",
    "academy",
    "programas",
    "descubrete",
    "prd",
    "life-circle",
    "life-map",
    "identity-map",
    "visionboard",
    "plan-vida",
    "priorizacion",
    "revision",
    "ikigai",
    "acompañamiento",
    "desarrollo",
    "herramientas",
    "reportes",
    "configuracion",
  ];
  optionsCustomerExecutive = [
    "dashboard",
    "onboarding",
    "results",
    "mi-mentor",
    "mentores-expertos",
    "planificacion",
    "academy",
    "programas",
    "descubrete",
    "prd",
    "life-circle",
    "life-map",
    "identity-map",
    "visionboard",
    "plan-vida",
    "priorizacion",
    "revision",
    "ikigai",
    // "acompañamiento",
    // "desarrollo",
    // "herramientas",
    // "reportes",
    // "configuracion",
  ];
  optionsCoach = ["dashboard-mentor", "users-assigned", "onboarding", "mentores-expertos", "agenda", "academy", "academy-vista", "ganancias", "configuracion"];
  optionsExpert = ["dashboard", "onboarding", "mentores-expertos", "agenda", "academy", "academy-vista", "ganancias", "configuracion"];
  optionsAdmin = [
    "dashboard",
    "admin-customers",
    "admin-coaches",
    "admin-experts",
    "admin-accounts",
    "admin-roles",
    "admin-videos-app",
    "admin-videos-academy",
    "configuracion",
  ];

  ngOnInit() {
    // Detectar la ruta actual al inicializar el componente
    // Usar setTimeout para asegurar que menuOptions esté inicializado
    setTimeout(() => {
      this.setActiveOptionFromCurrentRoute();
    }, 0);

    // Suscribirse a los cambios de ruta
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setActiveOptionFromCurrentRoute();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Limpiar caché cuando cambie el usuario
    if (changes['user'] && !changes['user'].firstChange) {
      this.clearCacheOnUserChange();
    }
    
    // Limpiar caché cuando cambie cualquier input para recalcular
    if (changes['user'] || changes['isCollapsed'] || changes['isResponsiveMenu']) {
      this.clearCache();
    }
  }

  setActiveOptionFromCurrentRoute() {
    const currentUrl = this.router.url;
    console.log('Current URL:', currentUrl);
    
    const dashboardPrefix = '/dashboard/';
    
    if (currentUrl.startsWith(dashboardPrefix)) {
      const fullRoute = currentUrl.substring(dashboardPrefix.length);
      // Extraer solo la ruta sin query parameters
      const route = fullRoute.split('?')[0];
      console.log('Full extracted route:', fullRoute);
      console.log('Route without query params:', route);
      console.log('Available menuOptions:', this.menuOptions);
      
      // Reset active states first
      this.activeOption = null;
      this.activeSubOption = null;
      
      // Buscar la opción principal que coincida con la ruta
      for (const option of this.menuOptions) {
        console.log('Checking option:', option.title, 'route:', option.route);
        
        // Verificar si la opción es visible antes de marcarla como activa
        if (option.route === route) {
          console.log('Found matching option:', option.title);
          this.activeOption = option;
          this.activeSubOption = null;
          return;
        }
        
        // Buscar en subopciones si existen
        if (option.subOptions && option.subOptions.length > 0) {
          for (const subOption of option.subOptions) {
            console.log('Checking subOption:', subOption.title, 'route:', subOption.route);
            // No marcar como activa si es un encabezado
            if (subOption.route === route && !subOption.isHeader) {
              console.log('Found matching subOption:', subOption.title);
              this.activeOption = option;
              this.activeSubOption = subOption;
              option.expanded = true; // Expandir el menú padre
              console.log('Active states set - activeOption:', this.activeOption?.title, 'activeSubOption:', this.activeSubOption?.title);
              return;
            }
          }
        }
      }
      
      console.log('No matching option found for route:', route);
    } else {
      console.log('URL does not start with dashboard prefix');
    }
  }
}
